import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Shell from "@/components/Shell";
import MoonDisc from "@/components/MoonDisc";
import EnsembleHero from "@/components/EnsembleHero";  // iter 31.302 — 277-agent group photo
import { api } from "@/lib/api";
import { Loader2, ArrowRight, Activity, RefreshCw } from "lucide-react";
import {
    LUNAR_META, CIRCADIAN_META,
    lunarMeta, circadianMeta, browserTzOffsetHours,
} from "@/lib/overlay";

/**
 * Spy Finance · Home · "Build Engine / Agent Three"
 *
 * Lunar-shift dashboard. Hero is the lunar arc (current phase highlighted) +
 * the circadian-band entry counts.
 *
 * Iter 31.22 (23 May 2026) — slow-load fix. Old version called the heavy
 * /shed/overlay/ledger?limit=1000 endpoint (two scans, full entry list)
 * just to read the aggregate counts. Now uses the lean cached
 * /shed/overlay/home-stats endpoint:
 *   1. First render: cached numbers (if any) appear instantly.
 *   2. If cache is stale (>30s old) or absent, a background `force=true`
 *      refresh fires — the "Recalculating · last computed Xs ago" pill
 *      surfaces while it runs.
 *   3. When the refresh returns, numbers swap in place without a flicker.
 *
 * Canon Rule 1 honoured: nothing here writes (the home_stats_cache is a
 * server-side derivation, not user-filed data).
 */
export default function Home() {
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const fetchStats = useCallback(async (opts = {}) => {
        const { force = false, background = false } = opts;
        if (background) setRefreshing(true);
        try {
            const tz = browserTzOffsetHours();
            const { data } = await api.get("/shed/overlay/home-stats", {
                params: { tz_offset_hours: tz, force: force ? "true" : "false" },
            });
            setStats(data);
            return data;
        } catch (_) {
            return null;
        } finally {
            if (background) setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            // 1. First paint — cached fetch is fast even on cold cache (single scan).
            const first = await fetchStats({ force: false });
            if (cancelled) return;

            // 2. Recent-ticker fetch in parallel — small, untouched.
            try {
                const { data: lg } = await api.get("/shed/ledger", { params: { limit: 8 } });
                if (!cancelled) setRecent(lg?.entries || []);
            } catch (_) { /* swallow */ }

            setLoading(false);

            // 3. If the cached numbers were stale, kick a background refresh.
            if (first && first.stale) {
                fetchStats({ force: true, background: true });
            }
        })();
        return () => { cancelled = true; };
    }, [fetchStats]);

    // Iter 31.22: stats payload has the same shape whether cached or fresh.
    const total = stats?.total_count_documents ?? 0;
    const today = stats?.tonight || null;
    const byLunar = stats?.by_lunar || {};
    const byCircadian = stats?.by_circadian || {};

    // Pre-compute "logged in darkness" vs "logged in daylight"
    // Darkness = dead-of-night + predawn + night
    // Daylight = morning + late-morning + midday + afternoon + evening
    const darknessKeys = ["dead-of-night", "predawn", "night"];
    const daylightKeys = ["morning", "late-morning", "midday", "afternoon", "evening"];
    const inDarkness = darknessKeys.reduce((n, k) => n + (byCircadian[k] || 0), 0);
    const inDaylight = daylightKeys.reduce((n, k) => n + (byCircadian[k] || 0), 0);

    // Top-3 most active circadian bands for the band readouts
    const topBands = Object.entries(byCircadian)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3);

    // Lunar arc — render all 8 phases in canonical order. Active = today's phase.
    const lunarOrder = [
        "new", "waxing-crescent", "first-quarter", "waxing-gibbous",
        "full", "waning-gibbous", "last-quarter", "waning-crescent",
    ];
    const todayLunarKey = today?.lunar?.label;

    // "Recalculating" pill copy
    const age = stats?.age_seconds;
    const computedLabel = (() => {
        if (age === null || age === undefined) return null;
        if (age < 5) return "just now";
        if (age < 60) return `${Math.round(age)}s ago`;
        if (age < 3600) return `${Math.round(age / 60)}m ago`;
        return `${Math.round(age / 3600)}h ago`;
    })();

    return (
        <Shell>
            <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Build Engine · Agent Three</div>
                    <h1 className="font-heading text-5xl md:text-6xl tracking-[0.04em] uppercase text-ink mt-1">
                        Tonight's Watch<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Built with a twenty-year horizon from day one. The data observes itself;
                        nothing is altered after filing. Originals stay where they were posted —
                        only the patterns rearrange.
                    </p>
                </div>
                <div className="pencil text-lg transform -rotate-2">
                    "1000 weeks = 19.2 years of context." — No. 2
                </div>
            </div>

            {/* Iter 31.134 — Recruitment strip. Subtle dossier-aesthetic
                CTA to /upgrade for any operative on a tier that isn't
                Cooperative. Not loud; not gated by tier check (Stripe
                gate lands iter 31.135) — for now everyone sees it as
                an open invitation per canon Rule 15. */}
            <Link
                to="/upgrade"
                data-testid="home-upgrade-strip"
                className="block mb-6 -mt-4 border border-ink/15 hover:border-stamp-red px-4 py-3 paper-card transition-colors group"
            >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                        <span className="font-heading uppercase text-[10px] tracking-[0.3em] text-stamp-red border border-stamp-red px-2 py-0.5">
                            Recruiting
                        </span>
                        <span className="font-body text-sm text-ink-soft group-hover:text-ink">
                            Operative and Cooperative tiers — open for application.
                        </span>
                    </div>
                    <span className="font-heading uppercase text-[10px] tracking-[0.25em] text-ink-soft group-hover:text-stamp-red inline-flex items-center gap-1">
                        Read the dossier <ArrowRight className="w-3 h-3" />
                    </span>
                </div>
            </Link>

            {loading && !stats ? (
                <div
                    className="paper-card p-12 flex items-center gap-3 text-ink-soft font-heading text-sm uppercase tracking-[0.3em]"
                    data-testid="home-loading"
                >
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Reading the room…
                </div>
            ) : (
                <>
                    {/* Cache freshness pill — shows whether numbers are live or cached,
                        and a manual recalc trigger. Hidden until stats arrive. */}
                    {stats && (
                        <div
                            className="mb-4 flex items-center justify-between gap-3 flex-wrap"
                            data-testid="home-cache-pill"
                        >
                            <div className="label-typed text-[10px] tracking-[0.22em] flex items-center gap-2">
                                {refreshing ? (
                                    <>
                                        <Loader2 className="w-3 h-3 animate-spin text-stamp-red" />
                                        <span className="text-stamp-red">Recalculating…</span>
                                        <span className="text-ink-faint">
                                            · last computed {computedLabel || "—"}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span className={stats.cached ? "text-ink-faint" : "text-stamp-green"}>
                                            {stats.cached ? "Cached" : "Fresh"}
                                        </span>
                                        <span className="text-ink-faint">
                                            · computed {computedLabel || "—"}
                                        </span>
                                        {stats.stale && (
                                            <span className="text-stamp-red ml-1">· stale</span>
                                        )}
                                    </>
                                )}
                            </div>
                            <button
                                onClick={() => fetchStats({ force: true, background: true })}
                                disabled={refreshing}
                                data-testid="home-recalc-btn"
                                className="font-heading uppercase text-[9px] tracking-[0.22em] text-ink-soft hover:text-stamp-red inline-flex items-center gap-1 disabled:opacity-40"
                                title="Recompute the watch immediately"
                            >
                                <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                                Recalculate
                            </button>
                        </div>
                    )}
                    {/* Iter 31.302 — 277-agent ensemble hero. Hides itself
                        gracefully until the winning group photo lands at
                        _Filed/generated/spy_finance_ensemble_v1.png on Drive. */}
                    <EnsembleHero />
                    {/* HERO — lunar arc + tonight's phase */}
                    <div
                        className="paper-card sf-cube p-6 md:p-10 mb-6 relative overflow-hidden"
                        data-testid="home-hero"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 items-center">
                            {/* Tonight's phase, big readout */}
                            <div className="md:col-span-1">
                                <div className="label-typed text-stamp-red">Tonight</div>
                                <div className="mt-2 flex items-baseline gap-3">
                                    <span
                                        className="text-stamp-red leading-none"
                                        aria-hidden
                                    >
                                        <MoonDisc
                                            phase={today?.lunar?.label || "full"}
                                            illumination={today?.lunar?.illumination || 0}
                                            size={96}
                                            color="currentColor"
                                            data-testid="tonight-moon"
                                        />
                                    </span>
                                    <div>
                                        <div className="font-heading text-xl text-ink uppercase tracking-[0.08em]">
                                            {lunarMeta(today?.lunar?.label).label}
                                        </div>
                                        <div className="label-typed mt-1">
                                            {Math.round((today?.lunar?.illumination || 0) * 100)}% illuminated
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-4 flex items-baseline gap-3">
                                    <span className="font-heading text-2xl text-ink">
                                        {circadianMeta(today?.circadian?.label).label}
                                    </span>
                                    <span className="label-typed">{today?.circadian?.hour_local}:00 local</span>
                                </div>
                            </div>

                            {/* Lunar arc — 8 phases laid out as a banner */}
                            <div className="md:col-span-2">
                                <div className="label-typed mb-3">Lunar arc · the watch wheel</div>
                                <div className="grid grid-cols-8 gap-1.5" data-testid="lunar-arc">
                                    {lunarOrder.map((k) => {
                                        const m = LUNAR_META[k];
                                        const active = todayLunarKey === k;
                                        return (
                                            <div
                                                key={k}
                                                title={m.label}
                                                data-testid={`lunar-phase-${k}`}
                                                className={`flex flex-col items-center gap-1 py-2 border ${
                                                    active
                                                        ? "border-stamp-red bg-stamp-red/15 sf-glow"
                                                        : "border-ink/15 bg-paper-dark/30"
                                                }`}
                                            >
                                                <span
                                                    className={`font-heading text-2xl ${active ? "text-stamp-red" : "text-ink-soft"}`}
                                                    aria-hidden
                                                >
                                                    {m.glyph}
                                                </span>
                                                <span className={`label-typed text-[8px] ${active ? "text-stamp-red" : ""}`}>
                                                    {m.label}
                                                </span>
                                                {(byLunar[k] || 0) > 0 && (
                                                    <span className="font-heading text-[10px] text-ink">
                                                        {byLunar[k]}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="label-typed text-[9px] mt-2 text-ink-faint">
                                    Counts under each phase = filings made when the moon was at that phase across all history.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SUMMARY STRIPE — Total / Darkness / Daylight + CTA */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
                        <div className="paper-card p-5" data-testid="home-stat-total">
                            <div className="label-typed">Total filings</div>
                            <div className="font-heading text-5xl text-stamp-red mt-1">{total}</div>
                            <div className="label-typed mt-1 text-[9px]">Across the Ledger</div>
                        </div>
                        <div className="paper-card p-5" data-testid="home-stat-darkness">
                            <div className="label-typed">Logged in darkness</div>
                            <div className="font-heading text-5xl text-ink mt-1">{inDarkness}</div>
                            <div className="label-typed mt-1 text-[9px]">
                                Dead-of-night · Predawn · Night
                            </div>
                        </div>
                        <div className="paper-card p-5" data-testid="home-stat-daylight">
                            <div className="label-typed">Logged in daylight</div>
                            <div className="font-heading text-5xl text-ink mt-1">{inDaylight}</div>
                            <div className="label-typed mt-1 text-[9px]">
                                Morning through evening
                            </div>
                        </div>
                        <Link
                            to="/shredder"
                            data-testid="home-cta-shift"
                            className="paper-card p-5 flex flex-col justify-between hover:border-stamp-red transition-colors group"
                        >
                            <div>
                                <div className="label-typed text-stamp-red">Begin shift</div>
                                <div className="font-heading text-2xl text-ink mt-2 uppercase tracking-[0.06em]">
                                    Post to the Shredder
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-2 text-stamp-red group-hover:translate-x-1 transition-transform">
                                <ArrowRight className="w-4 h-4" />
                                <span className="font-heading text-xs uppercase tracking-[0.22em]">Open intake</span>
                            </div>
                        </Link>
                    </div>

                    {/* CIRCADIAN BAND READOUTS — top 3 active bands */}
                    {topBands.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6" data-testid="home-bands">
                            {topBands.map(([k, n]) => {
                                const m = circadianMeta(k);
                                return (
                                    <div key={k} className="paper-card p-5" data-testid={`home-band-${k}`}>
                                        <div className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] font-heading ${m.cls}`}>
                                            {m.label}
                                        </div>
                                        <div className="font-heading text-4xl text-ink mt-3">{n}</div>
                                        <div className="label-typed text-[9px] mt-1">entries filed during this band</div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* RECENT TICKER */}
                    <div className="paper-card p-5" data-testid="home-recent">
                        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                            <div className="label-typed tw-prefix">Most recent transmissions</div>
                            <Link
                                to="/ledger"
                                data-testid="home-open-ledger"
                                className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft hover:text-stamp-red inline-flex items-center gap-1"
                            >
                                Open Ledger <ArrowRight className="w-3 h-3" />
                            </Link>
                        </div>
                        {recent.length === 0 ? (
                            <div className="label-typed text-ink-faint">Nothing in the wire yet.</div>
                        ) : (
                            <ul className="space-y-2">
                                {recent.slice(0, 6).map((e) => (
                                    <li
                                        key={e.id}
                                        className="flex items-start gap-3 border-b border-ink/10 pb-2 last:border-0 font-body text-sm text-ink"
                                    >
                                        <Activity className="w-3 h-3 mt-1 text-ink-soft shrink-0" strokeWidth={1.5} />
                                        <div className="flex-1 min-w-0">
                                            <div className="truncate">{e.summary}</div>
                                            <div className="label-typed text-[9px] mt-0.5">
                                                {e.timestamp?.slice(0, 16).replace("T", " ")} · {e.domain}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </Shell>
    );
}
